import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, merge } from 'rxjs';

export enum Theme {
  LIGHT = 'light',
  DARK = 'dark',
  SYSTEM = 'system'
}

export type HighContrastPreference = 'on' | 'off' | 'system';

export enum FontScale {
  DEFAULT = 1,
  MEDIUM = 1.125,
  LARGE = 1.25,
  EXTRA_LARGE = 1.5
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {
  private readonly THEME_STORAGE_KEY = 'bresearch-theme';
  private readonly HIGH_CONTRAST_STORAGE_KEY = 'bresearch-high-contrast';
  private readonly FONT_SCALE_STORAGE_KEY = 'bresearch-font-scale';
  private readonly MIN_FONT_SCALE = 1;
  private readonly MAX_FONT_SCALE = 1.5;
  private readonly DEFAULT_THEME = Theme.LIGHT;
  private readonly DEFAULT_HIGH_CONTRAST: HighContrastPreference = 'off';
  private readonly DEFAULT_FONT_SCALE = FontScale.DEFAULT;

  private currentTheme$ = new BehaviorSubject<Theme>(this.getCurrentTheme());
  private currentHighContrast$ = new BehaviorSubject<boolean>(false);
  private currentFontScale$ = new BehaviorSubject<number>(this.getFontScale());
  private themeChangeListener: any;
  private storageChangeListener: any;
  private highContrastChangeListener: any;
  private fontScaleStorageListener: any;

  constructor() {
    // Apply the saved theme on service initialization
    this.applyTheme(this.getCurrentTheme());

    // Apply high contrast preference
    this.applyHighContrast(this.getHighContrastPreference());

    // Apply font scale preference
    this.applyFontScale(this.getFontScale());

    // Listen for system theme changes if system theme is selected
    this.listenForSystemThemeChanges();

    // Listen for localStorage changes to bresearch-theme key
    this.setupStorageChangeListener();

    // Listen for system high contrast changes
    this.listenForSystemHighContrastChanges();

    // Listen for font scale localStorage changes
    this.setupFontScaleStorageListener();
  }

  /**
   * Get the current theme as an Observable
   */
  getTheme(): Observable<Theme> {
    return this.currentTheme$.asObservable();
  }

  /**
   * Get the current theme color as an Observable (alias for compatibility)
   */
  getThemeColor(): Observable<string> {
    return this.currentTheme$.asObservable() as Observable<string>;
  }

  /**
   * Get the current theme value synchronously
   */
  getCurrentTheme(): Theme {
    const savedTheme = localStorage.getItem(this.THEME_STORAGE_KEY);
    return (savedTheme as Theme) || this.DEFAULT_THEME;
  }

  /**
   * Get the current theme color synchronously (alias for compatibility)
   */
  getCurrentThemeColor(): string {
    return this.currentTheme$.value;
  }

  /**
   * Set a new theme
   * @param theme The theme to apply
   */
  setTheme(theme: Theme): void {
    if (Object.values(Theme).includes(theme)) {
      localStorage.setItem(this.THEME_STORAGE_KEY, theme);
      this.currentTheme$.next(theme);
      this.applyTheme(theme);
      // Re-apply high contrast to handle dark + high contrast combo
      this.applyHighContrast(this.getHighContrastPreference());
    }
  }

  /**
   * Apply the theme by setting data-theme attribute on document element
   * and updating the internal state
   * @param theme The theme to apply
   */
  private applyTheme(theme: Theme): void {
    let actualTheme = theme;

    // If system theme is selected, detect the current system preference
    if (theme === Theme.SYSTEM) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
      actualTheme = prefersDark.matches ? Theme.DARK : Theme.LIGHT;
    }

    document.documentElement.setAttribute('data-theme', actualTheme);
    this.currentTheme$.next(actualTheme);
  }

  /**
   * Listen for system theme changes when system theme is selected
   */
  private listenForSystemThemeChanges(): void {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    if (prefersDark) {
      this.themeChangeListener = (e: MediaQueryListEvent) => {
        // Only apply if system theme is selected
        if (this.getCurrentTheme() === Theme.SYSTEM) {
          this.applyTheme(Theme.SYSTEM);
          // Re-apply high contrast to handle dark + high contrast combo
          this.applyHighContrast(this.getHighContrastPreference());
        }
      };
      prefersDark.addEventListener('change', this.themeChangeListener);
    }
  }

  /**
   * Set up listener for localStorage 'bresearch-theme' changes
   */
  private setupStorageChangeListener(): void {
    this.storageChangeListener = (event: StorageEvent) => {
      // Listen for changes to bresearch-theme key
      if (event.key === this.THEME_STORAGE_KEY) {
        this.applyTheme(this.getCurrentTheme());
      }
      // Listen for changes to bresearch-high-contrast key
      if (event.key === this.HIGH_CONTRAST_STORAGE_KEY) {
        this.applyHighContrast(this.getHighContrastPreference());
      }
    };
    window.addEventListener('storage', this.storageChangeListener);
  }

  /**
   * Manually trigger theme update (useful when theme changes in localStorage)
   */
  refreshTheme(): void {
    this.applyTheme(this.getCurrentTheme());
  }

  /**
   * Get all available themes
   */
  getAvailableThemes(): Theme[] {
    return Object.values(Theme);
  }

  /**
   * Get theme display name
   * @param theme The theme to get the display name for
   */
  getThemeDisplayName(theme: Theme): string {
    const displayNames: { [key in Theme]: string } = {
      [Theme.LIGHT]: 'MY_ACCOUNT.USER_PREFERENCE.THEME.LIGHT',
      [Theme.DARK]: 'MY_ACCOUNT.USER_PREFERENCE.THEME.DARK',
      [Theme.SYSTEM]: 'MY_ACCOUNT.USER_PREFERENCE.THEME.SYSTEM'
    };
    return displayNames[theme];
  }

  /**
   * Get the current high contrast preference
   */
  getHighContrastPreference(): HighContrastPreference {
    const saved = localStorage.getItem(this.HIGH_CONTRAST_STORAGE_KEY);
    return (saved as HighContrastPreference) || this.DEFAULT_HIGH_CONTRAST;
  }

  /**
   * Set high contrast preference
   * @param preference The high contrast preference to apply
   */
  setHighContrastPreference(preference: HighContrastPreference): void {
    localStorage.setItem(this.HIGH_CONTRAST_STORAGE_KEY, preference);
    this.applyHighContrast(preference);
  }

  /**
   * Apply high contrast based on user preference and system settings
   * @param userPreference The user's high contrast preference
   */
  applyHighContrast(userPreference: HighContrastPreference): void {
    const html = document.documentElement;

    const systemPrefersHighContrast = window.matchMedia(
      '(prefers-contrast: more)'
    ).matches;

    const shouldEnableHighContrast =
      userPreference === 'on' ||
      (userPreference === 'system' && systemPrefersHighContrast);

    html.classList.toggle(
      'ion-palette-high-contrast',
      shouldEnableHighContrast
    );
    this.currentHighContrast$.next(shouldEnableHighContrast);
  }

  /**
   * Get the current high contrast active state as an Observable
   */
  getHighContrast$(): Observable<boolean> {
    return this.currentHighContrast$.asObservable();
  }

  /**
   * Emits whenever the active theme OR high-contrast state changes.
   *
   * `applyTheme` / `applyHighContrast` update the DOM (the `data-theme`
   * attribute and `ion-palette-high-contrast` class) *before* their subjects
   * emit, so subscribers that read `document.documentElement` (e.g. the
   * canonical contrast utilities / `themeColor` pipe) always observe the fresh
   * theme state. This is the signal that drives render-time study-color
   * re-computation immediately on toggle, without a page reload.
   */
  get themeChange$(): Observable<unknown> {
    return merge(this.currentTheme$, this.currentHighContrast$);
  }

  /**
   * Listen for system high contrast preference changes
   */
  private listenForSystemHighContrastChanges(): void {
    const systemHighContrastQuery = window.matchMedia(
      '(prefers-contrast: more)'
    );
    if (systemHighContrastQuery) {
      this.highContrastChangeListener = (e: MediaQueryListEvent) => {
        const userPref = this.getHighContrastPreference();
        // Only apply if system preference is selected
        if (userPref === 'system') {
          this.applyHighContrast(userPref);
        }
      };
      systemHighContrastQuery.addEventListener(
        'change',
        this.highContrastChangeListener
      );
    }
  }

  /**
   * Get available high contrast preferences
   */
  getAvailableHighContrastPreferences(): HighContrastPreference[] {
    return ['off', 'on', 'system'];
  }

  /**
   * Get display name for high contrast preference
   * @param preference The preference to get the display name for
   */
  getHighContrastDisplayName(preference: HighContrastPreference): string {
    const displayNames: { [key in HighContrastPreference]: string } = {
      on: 'MY_ACCOUNT.USER_PREFERENCE.HIGH_CONTRAST.ON',
      off: 'MY_ACCOUNT.USER_PREFERENCE.HIGH_CONTRAST.OFF',
      system: 'MY_ACCOUNT.USER_PREFERENCE.HIGH_CONTRAST.SYSTEM'
    };
    return displayNames[preference];
  }

  /**
   * Get a safe font scale value from storage.
   *
   */
  getFontScale(): number {
    const saved = localStorage.getItem(this.FONT_SCALE_STORAGE_KEY);
    if (!saved) {
      return this.DEFAULT_FONT_SCALE;
    }

    const parsed = parseFloat(saved);

    // If parsed value is not finite (NaN/Infinity), reset storage to default and return default.
    if (!Number.isFinite(parsed)) {
      localStorage.setItem(
        this.FONT_SCALE_STORAGE_KEY,
        this.DEFAULT_FONT_SCALE.toString()
      );
      return this.DEFAULT_FONT_SCALE;
    }
    // Clamp valid numeric values to the configured min/max range.
    const clamped = Math.min(
      this.MAX_FONT_SCALE,
      Math.max(this.MIN_FONT_SCALE, parsed)
    );

    // Persist the clamped value when correction is needed.
    if (clamped !== parsed) {
      localStorage.setItem(this.FONT_SCALE_STORAGE_KEY, clamped.toString());
    }

    return clamped;
  }

  /**
   * Get the current font scale as an Observable
   */
  getFontScale$(): Observable<number> {
    return this.currentFontScale$.asObservable();
  }

  /**
   * Set font scale
   * @param scale The font scale value to apply
   */
  setFontScale(scale: number): void {
    if (scale >= this.MIN_FONT_SCALE && scale <= this.MAX_FONT_SCALE) {
      localStorage.setItem(this.FONT_SCALE_STORAGE_KEY, scale.toString());
      this.currentFontScale$.next(scale);
      this.applyFontScale(scale);
    }
  }

  /**
   * Apply font scale by updating CSS variable
   * @param scale The font scale value to apply
   */
  private applyFontScale(scale: number): void {
    document.documentElement.style.setProperty(
      '--global-font-scale',
      scale.toString()
    );
    document.documentElement.setAttribute('data-font-scale', scale.toString());
  }

  /**
   * Set up listener for localStorage 'bresearch-font-scale' changes
   */
  private setupFontScaleStorageListener(): void {
    this.fontScaleStorageListener = (event: StorageEvent) => {
      if (event.key === this.FONT_SCALE_STORAGE_KEY) {
        const newScale = this.getFontScale();
        this.currentFontScale$.next(newScale);
        this.applyFontScale(newScale);
      }
    };
    window.addEventListener('storage', this.fontScaleStorageListener);
  }

  ngOnDestroy(): void {
    // Clean up theme change listener
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    if (prefersDark && this.themeChangeListener) {
      prefersDark.removeEventListener('change', this.themeChangeListener);
    }

    // Clean up storage change listener
    if (this.storageChangeListener) {
      window.removeEventListener('storage', this.storageChangeListener);
    }

    // Clean up font scale storage listener
    if (this.fontScaleStorageListener) {
      window.removeEventListener('storage', this.fontScaleStorageListener);
    }

    // Clean up high contrast change listener
    const prefersHighContrast = window.matchMedia('(prefers-contrast: more)');
    if (prefersHighContrast && this.highContrastChangeListener) {
      prefersHighContrast.removeEventListener(
        'change',
        this.highContrastChangeListener
      );
    }
  }
}
